/**
 * Renderer half of the agent execute-with-confirm protocol.
 *
 * An agent writes `<workspace>/execution.request.json`; the main process
 * validates it and raises `ai-workspace:execution-request`. This hook is what
 * the user actually sees and what closes the loop: it raises the confirmation
 * toast, sends exactly one decision, runs the tab, and always reports back so
 * `execution.response.json` gets written.
 *
 * Standing rules, all of them load-bearing:
 *
 * - **The request identity is captured when the prompt is raised.** Every
 *   message back to main carries that request's own `requestId`, never a
 *   re-read of "whatever is pending now" — otherwise a click meant for
 *   request A could authorise a queued request B for the same tab.
 * - **The SQL identity is captured the same way.** The prompt renders the tab
 *   content at event time while execution reads the editor buffer at click
 *   time, and an agent can rewrite `tabs/tab_<id>.sql` in between. The shown
 *   SQL therefore travels with the request as `expectedSql`, and the tab-side
 *   runner executes only if the buffer still matches. What runs is what the
 *   user read, or nothing runs.
 * - **Nothing runs unless main accepted the decision.** A stale prompt (the
 *   5-minute deadline elapsed, the session ended) is refused there, and the
 *   user is told rather than left assuming their approval took effect.
 * - **A dismissed prompt is a denial.** An unanswered agent is the worst
 *   failure mode of this protocol, so closing the toast answers it.
 * - **Nothing from the engine leaves the renderer.** Only app-authored
 *   strings are reported back; the agent reads real detail from `.history/`.
 * - **The copy is agent-neutral** (see `executionRequestPrompt.ts`).
 *
 * Gated on Pro/Trial (wave decision D-6) to match AI Workspace sync. Main
 * gates independently — this is the UI half of the same rule, not the rule.
 */

import { useCallback, useEffect } from 'react';
import { useAnalytics } from '../../../core/analytics/useAnalytics';
import { useHasProAccess } from '../../../core/licensing';
import { toast } from '../../../components/ui/toaster';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import { isQueryTab } from '../../workspace/core/types/workspaceTypes';
import {
  useAiExecutionStore,
  AI_EXECUTION_SQL_CHANGED,
  AI_EXECUTION_TARGET_CHANGED,
  AI_EXECUTION_NOT_CONFIRMED,
} from '../store/aiExecutionStore';
import { showExecutionRequestToast } from './executionRequestToast';
import { isGrantedForTarget, rememberSessionGrant, type ExecutionTarget } from './executionGrantScope';
import type { ExecutionPromptTarget } from './executionRequestPrompt';
import type { ExecutionRequestedEvent } from '../../../types/aiWorkspace';

/**
 * App-authored outcomes. Engine error text is deliberately never written to
 * `execution.response.json`: it can carry server names, logins and paths, and
 * the agent already gets the real message from the workspace history artifact.
 */
const EXECUTION_FAILED = 'Query execution failed.';
const SQL_CHANGED = 'The query changed after it was shown for approval. Nothing ran.';
const TARGET_CHANGED =
  "The tab's connection or database changed after it was shown for approval. Nothing ran.";
const NOT_CONFIRMED = 'The user did not confirm this query. Nothing ran.';

/** Monotonic suffix that keeps store request ids unique within a millisecond. */
let runCounter = 0;

export function useAiExecutionRequests(): void {
  const canUseAiWorkspace = useHasProAccess();
  const { track } = useAnalytics();

  /**
   * Run the tab and report the outcome, whatever it is.
   *
   * `approvedSql` is the exact text the user consented to. It is forwarded so
   * the tab-side runner can refuse if the editor buffer no longer holds it.
   * It is absent only where no prompt was shown (a session-scoped grant), and
   * it is never used AS the SQL to run — only as the thing the buffer must
   * still equal.
   *
   * `target` is the same idea for where it lands. It is passed on BOTH paths:
   * even under a grant, the target was checked at event time, so the run must
   * be pinned to it or a rebind could redirect an approved statement.
   */
  const executeAndReport = useCallback(async (
    event: ExecutionRequestedEvent,
    target: ExecutionTarget,
    approvedSql?: string,
  ) => {
    const api = window.electronAPI?.aiWorkspace;
    if (!api) return;

    const result = await useAiExecutionStore.getState().addRequest({
      // Unique per run: the tab-side runner de-duplicates on this id, so two
      // requests landing in the same millisecond must not share one.
      requestId: `ai-workspace-exec-${Date.now()}-${(runCounter += 1)}`,
      tabId: event.tabId,
      awaitCompletion: true,
      requestedAt: Date.now(),
      ...(approvedSql !== undefined ? { expectedSql: approvedSql } : {}),
      expectedTarget: target,
    });

    const targetChanged = result.success !== true && result.error === AI_EXECUTION_TARGET_CHANGED;
    if (targetChanged) {
      toast.error({
        title: 'The connection changed — nothing ran',
        description:
          'This tab was pointed at a different connection or database after the prompt appeared, so nothing was executed. Ask the agent to request it again.',
      });
    }

    // A human declining the destructive-query dialog is not a failed run.
    const notConfirmed = result.success !== true && result.error === AI_EXECUTION_NOT_CONFIRMED;

    const sqlChanged = result.success !== true && result.error === AI_EXECUTION_SQL_CHANGED;
    if (sqlChanged) {
      // The user's approval was for text that is no longer there. Say so —
      // silently reporting "failed" would read as a database problem.
      toast.error({
        title: 'The query changed — nothing ran',
        // Deliberately not "the agent rewrote it": `expectedSql` is the tab
        // content at request time, so the user's own typing during the prompt
        // window lands here too. Say what happened, not who did it.
        description:
          'The tab changed after the prompt appeared, so your approval no longer matched it. Ask the agent to request it again.',
      });
    }

    await api
      .sendExecutionFinished({
        sessionId: event.sessionId,
        tabId: event.tabId,
        requestId: event.requestId,
        ok: result.success === true,
        ...(result.success === true
          ? {}
          : {
              error: sqlChanged
                ? SQL_CHANGED
                : targetChanged
                  ? TARGET_CHANGED
                  : notConfirmed
                    ? NOT_CONFIRMED
                    : EXECUTION_FAILED,
            }),
      })
      .catch(() => undefined);
  }, []);

  const promptForDecision = useCallback(
    (
      event: ExecutionRequestedEvent,
      tabName: string,
      sql: string,
      target: ExecutionPromptTarget,
      scopeTarget: ExecutionTarget
    ) => {
      showExecutionRequestToast({ tabName, sql, reason: event.reason, ...target }, (answer) => {
        void (async () => {
          const api = window.electronAPI?.aiWorkspace;
          if (!api) return;

          // A request main already approved (a standing grant we narrowed) is
          // not expecting a decision — it is waiting for a completion report.
          if (event.autoApproved) {
            track('feature:ai_exec_confirm', { choice: answer.choice });
            if (answer.scope === 'session') rememberSessionGrant(event.sessionId, scopeTarget);
            if (answer.approved) {
              await executeAndReport(event, scopeTarget, sql);
            } else {
              // Main accepts a refusal even for a request it already approved,
              // so the agent gets an honest `denied`. Reporting a finished-but
              // -failed run would tell it the query broke, not that a person
              // said no.
              await api
                .sendExecutionDecision({
                  sessionId: event.sessionId,
                  tabId: event.tabId,
                  requestId: event.requestId,
                  approved: false,
                  scope: 'once',
                })
                .catch(() => undefined);
            }
            return;
          }

          const decision = await api
            .sendExecutionDecision({
              sessionId: event.sessionId,
              tabId: event.tabId,
              // The id captured when THIS prompt was raised — never re-read
              // from whatever happens to be pending by the time of the click.
              requestId: event.requestId,
              approved: answer.approved,
              scope: answer.scope,
            })
            .catch(() => ({ success: false }));

          if (decision?.success !== true) {
            // Main already answered this request (deadline, session end, or a
            // newer request took its place). Say so rather than let the user
            // believe they approved something that then ran.
            if (answer.approved) {
              toast.default({
                title: 'That request already expired',
                description: 'Nothing ran. Ask the agent to request it again.',
              });
            }
            return;
          }

          // Fires on the decision landing, never on the click itself.
          track('feature:ai_exec_confirm', { choice: answer.choice });
          // A standing grant covers only the connection + database the user
          // was looking at when they gave it.
          if (answer.scope === 'session') rememberSessionGrant(event.sessionId, scopeTarget);
          // `sql` is what the toast rendered — the run is bound to it.
          if (answer.approved) await executeAndReport(event, scopeTarget, sql);
        })();
      });
    },
    [executeAndReport, track]
  );

  const handleRequest = useCallback(
    async (event: ExecutionRequestedEvent) => {
      const api = window.electronAPI?.aiWorkspace;
      if (!api) return;

      const tab = useWorkspaceStore.getState().tabs.find((t) => t.id === event.tabId);
      const queryTab = tab && isQueryTab(tab) ? tab : null;

      if (!queryTab) {
        // Main authorised the tab against the files it synced; the renderer is
        // the authority on what is actually open. Terminate the request so the
        // agent never waits on a response that can't come — and say WHY.
        // `tab-not-open` is a delivery failure, not a consent answer, so main
        // accepts it on the decision channel even for an auto-approved request;
        // reporting `user-denied` here would tell the agent a user refused
        // something no user ever saw.
        await api
          .sendExecutionDecision({
            sessionId: event.sessionId,
            tabId: event.tabId,
            requestId: event.requestId,
            approved: false,
            scope: 'once',
            declineReason: 'tab-not-open',
          })
          .catch(() => undefined);
        return;
      }

      const scopeTarget: ExecutionTarget = {
        connectionId: queryTab.connectionInfo?.connectionId,
        database: queryTab.connectionInfo?.database,
      };

      // A session-scoped allow is in effect — but main records it as a plain
      // per-session boolean, and every query tab is synced into every AI
      // session's workspace. So the grant only stands for the connection and
      // database it was actually given from; anything else asks again.
      if (event.autoApproved && isGrantedForTarget(event.sessionId, scopeTarget)) {
        // No prompt shown, no human decision made — `ai_exec_confirm` means "a
        // human confirmed something", so this is deliberately a separate,
        // sibling event (R1-b telemetry residual) rather than a firing of
        // that one under a synthetic `choice`.
        track('feature:ai_exec_autorun');
        await executeAndReport(event, scopeTarget);
        return;
      }

      // Every query tab is synced into EVERY AI session's workspace, so a
      // request raised from a dev terminal can target a tab bound to
      // production. The prompt must name where the query would land.
      promptForDecision(
        event,
        queryTab.title,
        queryTab.content?.query ?? '',
        {
          connectionName:
            queryTab.connectionInfo?.connectionName || queryTab.connectionInfo?.serverHost,
          database: queryTab.connectionInfo?.database,
        },
        scopeTarget
      );
    },
    [executeAndReport, promptForDecision, track]
  );

  useEffect(() => {
    if (!canUseAiWorkspace) return;
    const api = window.electronAPI?.aiWorkspace;
    if (!api?.onExecutionRequest) return;

    const unsubscribe = api.onExecutionRequest((event) => {
      void handleRequest(event);
    });

    return () => {
      unsubscribe?.();
    };
  }, [canUseAiWorkspace, handleRequest]);
}
